import React, { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Elements } from '@kontent-ai/delivery-sdk';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Droppable } from './Droppable';
import { Draggable } from './Draggable';
import Stack from '@mui/system/Stack';
import ComponentTitle from '../ComponentTitle';
import RichTextComponents from '../RichTextComponents';
import '../../CSS/SortingSequencing.css';

// Define a type for rich text items
interface RichTextItem {
  id: string;
  richText: Elements.RichTextElement;
}

const SortableExercise: React.FC<{ element: any }> = ({ element }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Extract data and create items safely
  const items = useMemo<RichTextItem[]>(() => {
    if (!element?.groups?.linkedItems?.[0]?.elements?.answers?.linkedItems) {
      return [];
    }
    return element.groups.linkedItems[0].elements.answers.linkedItems
      .filter((item: any) => item && item.elements) // Filter out null/undefined items
      .map((item: any, index: number) => ({
        id: `item-${index}`,
        richText: item.elements?.rich_text_answer || {
          value: item.elements?.answer?.value || '',
        },
      }));
  }, [element]);

  // Track just the IDs for SortableContext
  const [itemIds, setItemIds] = useState<string[]>([]);

  // Initialize itemIds when items are ready
  React.useEffect(() => {
    if (items.length > 0 && itemIds.length === 0) {
      setItemIds(items.map(item => item.id));
    }
  }, [items, itemIds.length]);

  // Store the correct order of IDs
  const correctOrder = useMemo(() => {
    return items.map((_, index) => `item-${index}`);
  }, [items]);

  // Use MouseSensor + TouchSensor instead of PointerSensor for reliable mobile support.
  // PointerSensor cannot preventDefault on touch devices, causing the browser's default
  // scroll/zoom to cancel drags. TouchSensor handles this correctly via touchmove events.
  // Both sensors use a distance constraint (no delay) because touch-action: none on
  // draggable elements prevents the browser from intercepting touch events, eliminating
  // the iOS stutter that occurred when delay: 50 let the browser start scroll handling.
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      // With touch-action: none on draggable elements, the browser won't try to scroll,
      // so we can use a distance constraint instead of a delay for instant response on iOS.
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  ); // Randomize item order on first load - only run once
  React.useEffect(() => {
    if (itemIds.length > 0 && !isInitialized) {
      setItemIds(prevIds => {
        // Only shuffle if we have more than 1 item
        if (prevIds.length <= 1) {
          return prevIds;
        }

        let shuffled;
        let attempts = 0;
        const maxAttempts = 10; // Prevent infinite loops

        do {
          shuffled = [...prevIds].sort(() => Math.random() - 0.5);
          attempts++;
        } while (
          JSON.stringify(shuffled) === JSON.stringify(correctOrder) &&
          attempts < maxAttempts
        );

        return shuffled;
      });
      setIsInitialized(true);
    }
  }, [itemIds.length, correctOrder, isInitialized]);

  // State to track whether the order is correct
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  // Function to handle the submit action
  const handleSubmit = (): void => {
    const isOrderCorrect =
      JSON.stringify(itemIds) === JSON.stringify(correctOrder);
    setIsCorrect(isOrderCorrect);
    setIsSubmitted(true);
  };

  // Function to get the style for each item
  const getItemStyle = (id: string, index: number): React.CSSProperties => {
    const correctIndex = correctOrder.indexOf(id);
    const isIncorrect = isCorrect === false && correctIndex !== index;

    return {
      padding: '10px',
      border: '1px solid black',
      borderRadius: '4px', // Reduced border radius for a more square-round look
      backgroundColor:
        isCorrect === null || isDragging
          ? 'lightgrey'
          : isIncorrect
            ? '#ffcccc'
            : '#ccffcc',
      color: 'black',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      cursor: 'grab',
      textAlign: 'left',
      fontSize: '16px',
      fontFamily: 'Open Sans, sans-serif',
      fontWeight: '400',
      // 'none' gives dnd-kit full control of touch events immediately, eliminating
      // the iOS stutter caused by 'manipulation' which still allows pan gestures.
      touchAction: 'none',
      WebkitUserSelect: 'none',
      userSelect: 'none',
    } as React.CSSProperties;
  };

  // Effect to reset correctness when dragging starts
  React.useEffect(() => {
    if (isDragging) {
      setIsCorrect(null);
    }
  }, [isDragging]);
  return (
    <div>
      <div className="sequence-container da-panel-primary">
        {element.title && <ComponentTitle title={element.title.value} />}
        {element.body?.value && element.body.value.trim() !== '<p><br></p>' && (
          <div>
            <RichTextComponents element={element.body} />
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event): void => {
            setIsDragging(false);
            handleDragEnd(event);
          }}
          onDragStart={(): void => setIsDragging(true)}
          onDragCancel={(): void => setIsDragging(false)}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={2} style={{ flexWrap: 'wrap' }}>
              {itemIds.map((id, index) => {
                const item = items.find(i => i.id === id);
                if (!item) return null;

                return (
                  <Droppable key={id} id={id}>
                    <Draggable id={id}>
                      <div style={getItemStyle(id, index)}>
                        <RichTextComponents element={item.richText} />
                      </div>
                    </Draggable>
                  </Droppable>
                );
              })}
            </Stack>
          </SortableContext>
        </DndContext>

        {/* button to submit the order */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
          }}
        >
          <button onClick={handleSubmit} className="cta-content-button">
            Submit
          </button>
        </div>

        {/* Display feedback based on correctness */}
        {isCorrect !== null && (
          <div
            style={{
              marginTop: '10px',
              color: isCorrect ? 'green' : 'red',
              justifyContent: 'center',
              textAlign: 'center',
              fontSize: '20px',
            }}
          >
            {isCorrect ? 'Correct Order!' : 'Incorrect Order. Try Again.'}
          </div>
        )}
      </div>
    </div>
  );

  // Function to handle the drag end event
  function handleDragEnd(event: any): void {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = itemIds.indexOf(active.id);
      const newIndex = itemIds.indexOf(over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        setItemIds(prev => arrayMove(prev, oldIndex, newIndex));
      }
    }

    // Reset submission state when order changes
    if (isSubmitted) {
      setIsSubmitted(false);
      setIsCorrect(null);
    }
  }
};

export default SortableExercise;
